// React.
import { useContext, useEffect, useState } from 'react';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { StatisticTimeslice } from '../../../types/instance/Statistic';

// Components.
import StatisticChart from '../../../component/instance/chart/StatisticChart';

// Helper.
import { fetchResults, stringFormatting } from '../../../helpers/utils';
import StatisticApi from '../../../api/instance/StatisticApi';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { showError } from '../../../utils/showAlert';

export default function Statistics(props: { instance: InstanceTarget }) {
    interface StatisticData {
        name: string,
        cumulative: boolean,
        data: StatisticTimeslice[]
    }

    const {instance} = props;

    const [statistics, setStatistics] = useState<StatisticData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [layout, setLayout] = useState<number>(() => {
        const savedLayout = localStorage.getItem('statisticsLayout');
        return savedLayout ? parseInt(savedLayout, 10) : 1;
    }); // 1, 2, or 3 columns
    const timeRangeContext = useContext(TimeRangeContext);

    const [selectedStatistics, setSelectedStatistics] = useState<string[]>([]);

    const handleLayoutChange = (newLayout: number) => {
        setLayout(newLayout);
        localStorage.setItem('statisticsLayout', newLayout.toString());
    };

    // Initialize selectedStatistics when statistics data is loaded
    useEffect(() => {
        if (statistics.length > 0) {
            setSelectedStatistics(statistics.map(stat => stat.name));
        }
    }, [statistics]);

    const handleStatisticToggle = (statisticName: string) => {
        setSelectedStatistics(prevSelected => {
            if (prevSelected.includes(statisticName)) {
                return prevSelected.filter(name => name !== statisticName);
            } else {
                return [...prevSelected, statisticName];
            }
        });
    };

    const handleSelectAllStatistics = (selectAll: boolean) => {
        if (selectAll) {
            setSelectedStatistics(statistics.map(stat => stat.name));
        } else {
            setSelectedStatistics([]);
        }
    };

    useEffect(() => {
        async function load() {
            try {
                setLoading(true);

                let promises: Promise<any>[] = [];
                let statsArray: StatisticData[] = [];

                const metadata: any[][] = await fetchResults
                ([
                    StatisticApi.fetchType(instance.type)
                ]);

                for (const m of metadata) {
                    for (const t of m) {
                        // There is no data until the promises have completed.
                        statsArray.push({
                            name: t.statistic,
                            cumulative: t.cumulative, data: []
                        });
                        promises.push(StatisticApi.fetchTime(buildApiQuery(timeRangeContext.timeRange), instance.id, t.statistic))
                    }
                }

                // Fetch all the data in parallel.
                const results: StatisticTimeslice[][] =
                        await fetchResults(promises);

                if (active) {
                    setLoading(false);

                    // Populate the data fields.
                    for (let i = 0; i < statsArray.length; i++) {
                        statsArray[i].data = results[i];
                    }

                    setStatistics(statsArray);
                    setLoading(false);
                }
            } catch (x: any) {
                console.log(x.message);
                const errorMessage = x.message || x.toString();
                setError(errorMessage);
                showError('Failed to load statistics', errorMessage);
                setStatistics([]);
                setLoading(false);
            }
        }

        let active: boolean = true;
        load();
        // console.log ('Leaving useEffect, active);
        return () => {
            // Clean up afterwards to avoid race conditions.
            // console.log ('Clean up');
            active = false;
        }
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, instance.id, instance.type]);

    // The outer div adds padding around the collapsible charts.
    return (
            <>
                {/* Filter and Layout toggle UI */}
                <div className="row">
                    <div className="col d-flex justify-content-between align-items-center mb-2">
                        <div className="btn-group dropdown">
                            {statistics.length > 0 && (
                                    <button type="button" id="filterStatistics" className="btn btn-dropdown dropdown-toggle"
                                            data-bs-toggle="dropdown" aria-expanded="false"
                                            style={{
                                                border: '1px solid var(--dbm-border-color)',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--dbm-bg-surface-alt)',
                                                color: 'var(--dbm-text-primary)'
                                            }}>
                                        Filter Charts
                                    </button>
                            )}
                            <div id="dropdown-menu-statistics" className="row g-0 dropdown-menu dropdown-menu-scroll"
                                 aria-labelledby="filterStatistics" onClick={(e) => e.stopPropagation()}>
                                <div className='select-all'>
                                    <span onClick={() => handleSelectAllStatistics(true)} style={{cursor: 'pointer', marginRight: '5px'}}>All</span>
                                    <label className='separator'>|</label>
                                    <span onClick={() => handleSelectAllStatistics(false)} style={{cursor: 'pointer', marginLeft: '5px'}}>None</span>
                                </div>
                                <div className='statistics-list'>
                                    {statistics.sort((a, b) => a.name.localeCompare(b.name)).map((stat, i) => (
                                            <div key={i}>
                                                <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`stat-option_${i}`}
                                                        checked={selectedStatistics.includes(stat.name)}
                                                        onChange={() => handleStatisticToggle(stat.name)}
                                                />
                                                <label htmlFor={`stat-option_${i}`}>{stringFormatting(stat.name)}</label>
                                            </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleLayoutChange(1)} style={{
                                marginRight: 5,
                                background: layout === 1 ? 'var(--dbm-bg-surface-hover)' : 'var(--dbm-bg-surface-alt)',
                                border: '1px solid var(--dbm-border-color)',
                                borderRadius: '4px',
                                color: 'var(--dbm-text-primary)'
                            }} title="1 column">
                                <i className="fal fa-list fa-lg" />
                            </button>
                            <button onClick={() => handleLayoutChange(2)} style={{
                                marginRight: 5,
                                background: layout === 2 ? 'var(--dbm-bg-surface-hover)' : 'var(--dbm-bg-surface-alt)',
                                border: '1px solid var(--dbm-border-color)',
                                borderRadius: '4px',
                                color: 'var(--dbm-text-primary)'
                            }} title="2 columns">
                                <i className="fal fa-th-large fa-lg" />
                            </button>
                            <button onClick={() => handleLayoutChange(3)} style={{
                                background: layout === 3 ? 'var(--dbm-bg-surface-hover)' : 'var(--dbm-bg-surface-alt)',
                                border: '1px solid var(--dbm-border-color)',
                                borderRadius: '4px',
                                color: 'var(--dbm-text-primary)'
                            }} title="3 columns">
                                <i className="fal fa-th fa-lg" />
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`row row-cols-${layout}`}>
                    {statistics
                            .filter(stat => selectedStatistics.includes(stat.name))
                            .map((statistic: StatisticData, index: number) => (
                                    <div className="col" key={index}>
                                        <StatisticChart error={error} loading={loading}
                                                        data={statistic.data} statistic={statistic.name}
                                                        cumulative={statistic.cumulative} index={index}
                                        />
                                    </div>
                            ))}
                </div>
            </>
    )
}
